/**
 * Load Google base functions
 */

(function (pmw, undefined) {

	pmw.googleConfigConditionsMet = ({type = null, log = false}) => {

		// Never load Google while the deny-by-default consent fallback is
		// active (the consent module failed to load). Even Google Consent Mode
		// must stay off in that state: without the real consent API the gtag
		// bootstrap cannot queue reliable consent signals, and the fallback
		// guarantees that no vendor request leaves the browser.
		if (pmw.consent?.isFallback === true) {
			if (log) pmw.console.log("Consent fallback state is active. Not loading the Google pixel.");
			return false;
		}

		// Always returns true if Google Consent Mode is active
		if (pmwDataLayer?.pixels?.google?.consent_mode?.is_active) {
			if (log) pmw.console.log("Google Consent Mode is active. Loading the Google pixel.");
			return true;
		}

		// If a type is passed, return true if the user has given consent to that type
		// or false if the user has not given consent to that type.
		if (type) {
			if (!!pmw.consent.categories.get()[type]) {
				if (log) pmw.console.log("The category " + type + " has been approved. Loading the Google pixel.");
				return true;
			} else {
				if (log) pmw.console.log("The category " + type + " has been denied. Not loading the Google pixel.");
				return false;
			}
		}

		// If no type is passed, return true if the user has given consent to the statistics or marketing categories.
		if (!!(pmw.consent.categories.get().marketing || pmw.consent.categories.get().statistics)) {
			if (log) pmw.console.log("The categories statistics and/or marketing are approved. Loading the Google pixel.");
			return true;
		}

		return false;
	};

	pmw.fireGtagGoogleAds = () => {
		try {
			pmwDataLayer.pixels.google.ads.state = "loading";

			const suppressedTags = pmwDataLayer?.pixels?.google?.tag_id_suppressed || [];

			if (pmwDataLayer?.pixels?.google?.enhanced_conversions?.is_active) {
				Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids).forEach((key) => {
					if (suppressedTags.includes(key)) {
						pmw.console.log("Skipping suppressed Google Ads tag: " + key);
						return;
					}
					gtag("config", key, {"allow_enhanced_conversions": true});
				});
			} else {
				Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids).forEach((key) => {
					if (suppressedTags.includes(key)) {
						pmw.console.log("Skipping suppressed Google Ads tag: " + key);
						return;
					}
					gtag("config", key);
				});
			}

			if (pmwDataLayer?.pixels?.google?.ads?.conversion_ids && pmwDataLayer?.pixels?.google?.ads?.phone_conversion_label && pmwDataLayer?.pixels?.google?.ads?.phone_conversion_number) {
				gtag("config", Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids)[0] + "/" + pmwDataLayer.pixels.google.ads.phone_conversion_label, {
					phone_conversion_number: pmwDataLayer.pixels.google.ads.phone_conversion_number,
				});
			}

			pmwDataLayer.pixels.google.ads.state = "ready";
		} catch (e) {
			console.error(e);
		}
	};

	// https://developers.google.com/tag-platform/gtagjs/reference
	pmw.fireGtagGoogleAnalyticsGA4 = () => {

		try {
			const measurementId = pmwDataLayer.pixels.google.analytics.ga4.measurement_id;
			const suppressedTags = pmwDataLayer?.pixels?.google?.tag_id_suppressed || [];

			if (suppressedTags.includes(measurementId)) {
				pmw.console.log("Skipping suppressed GA4 tag: " + measurementId);
				return;
			}

			pmwDataLayer.pixels.google.analytics.ga4.state = "loading";

			let parameters = pmwDataLayer.pixels.google.analytics.ga4.parameters;

			if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.debug_mode) {
				parameters.debug_mode = true;
			}

			gtag("config", measurementId, parameters);

			pmwDataLayer.pixels.google.analytics.ga4.state = "ready";
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Check if Google is active
	 *
	 * @returns {boolean}
	 */
	pmw.isGoogleActive = () => {

		// If GA4 is active, return true
		if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id) return true;

		// If Google Ads is active, return true
		if (!pmw.isEmptyObject(pmwDataLayer?.pixels?.google?.ads?.conversion_ids)) return true;

		return false;
	};

	pmw.getGoogleGtagId = () => {

		if (pmwDataLayer?.pixels?.google?.tag_id) {
			return pmwDataLayer.pixels.google.tag_id;
		}

		if (
			pmwDataLayer?.pixels?.google?.ads?.conversion_ids
			&& Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids)[0]
		) {
			return Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids)[0];
		}

		if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id) {
			return pmwDataLayer.pixels.google.analytics.ga4.measurement_id;
		}

		return "";
	};

	/**
	 * Session storage key for caching the GTG handler type
	 */
	const GTG_HANDLER_CACHE_KEY = "gtg_h";

	/**
	 * localStorage key for persistent cross-session GTG handler caching
	 */
	const GTG_HANDLER_PERSISTENT_KEY = "pmw_gtg_handler";

	/**
	 * Persistent cache TTL in milliseconds (6 hours)
	 */
	const GTG_HANDLER_PERSISTENT_TTL = 6 * 60 * 60 * 1000;

	/**
	 * Flag to prevent multiple detection requests on the same page
	 */
	let gtgDetectionInProgress = false;

	/**
	 * Valid handler types
	 */
	const VALID_HANDLERS = ["external", "standalone", "wordpress"];

	/**
	 * Get the GTG handler from session cache or persistent localStorage cache
	 *
	 * Server-side detection is unreliable for Cloudflare (server-to-server requests bypass CDN).
	 * Only use session storage cache from previous browser-side detection.
	 * Also checks localStorage for cross-session persistence (6h TTL).
	 *
	 * @returns {string|null} The handler type or null if not cached
	 */
	const getGtgHandler = () => {
		// Session storage cache from previous detection (same tab)
		try {
			const cached = pmw.retrieveData(GTG_HANDLER_CACHE_KEY);
			if (cached && VALID_HANDLERS.includes(cached)) {
				return cached;
			}
		} catch (e) {
			// Storage not available
		}

		// Persistent localStorage cache (survives tab close, 6h TTL)
		try {
			const stored = localStorage.getItem(GTG_HANDLER_PERSISTENT_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				if (parsed.expires > Date.now() && VALID_HANDLERS.includes(parsed.handler)) {
					// Restore to session storage for faster access on next check
					try {
						pmw.storeData(GTG_HANDLER_CACHE_KEY, parsed.handler);
					} catch (e) {
						// Silently fail
					}
					return parsed.handler;
				}
				// Expired, clean up
				localStorage.removeItem(GTG_HANDLER_PERSISTENT_KEY);
			}
		} catch (e) {
			// localStorage not available
		}

		return null;
	};

	/**
	 * Cache the GTG handler type in session storage and localStorage
	 *
	 * Session storage provides same-tab caching across page navigations.
	 * localStorage provides cross-session persistence with a 6h TTL,
	 * so returning visitors skip detection entirely.
	 *
	 * @param {string} handler - The handler type (e.g., "wordpress", "standalone", "external")
	 */
	const cacheGtgHandler = (handler) => {
		// Session storage (same tab, dies on close)
		try {
			pmw.storeData(GTG_HANDLER_CACHE_KEY, handler);
		} catch (e) {
			// Silently fail if storage is not available
		}

		// Persistent localStorage (survives tab close, 6h TTL)
		try {
			localStorage.setItem(GTG_HANDLER_PERSISTENT_KEY, JSON.stringify({
				handler: handler,
				expires: Date.now() + GTG_HANDLER_PERSISTENT_TTL
			}));
		} catch (e) {
			// Silently fail if localStorage is not available
		}

		// Cookie for PHP server-side reading (7-day expiry)
		// PHP reads this to avoid wp_remote_get() self-probing for handler detection
		try {
			pmw.setCookie("pmw_gtg_handler", handler, 7);
		} catch (e) {
			// Silently fail if cookies are not available
		}
	};

	/**
	 * Detect which GTG handler is responding (synchronous/blocking)
	 *
	 * Makes preflight requests to detect the optimal proxy configuration.
	 *
	 * Detection priority (fastest to slowest):
	 * 1. External proxy (Cloudflare) via measurement_path - fastest (ALWAYS checked)
	 * 2. Server-provided handler hint from data layer (standalone or wordpress)
	 * 3. Standalone proxy via proxy_url - fast, bypasses WordPress
	 * 4. WordPress proxy via measurement_path - slowest, full WordPress load
	 *
	 * This function returns a Promise that resolves to the handler type.
	 *
	 * Possible handlers:
	 * - "external": External proxy like Cloudflare (fastest) - use measurement_path
	 * - "standalone": Standalone proxy PHP file (fast, no WordPress) - use proxy_url
	 * - "wordpress": WordPress fallback (slowest) - use measurement_path
	 *
	 * @returns {Promise<string>} The detected handler type
	 */
	const detectGtgHandler = async () => {
		// Prevent multiple detection requests on the same page
		if (gtgDetectionInProgress) {
			// Wait for existing detection to complete
			return new Promise((resolve) => {
				const checkInterval = setInterval(() => {
					const cached = getGtgHandler();
					if (cached) {
						clearInterval(checkInterval);
						resolve(cached);
					}
				}, 10);
				// Timeout after 2 seconds
				setTimeout(() => {
					clearInterval(checkInterval);
					resolve("wordpress");
				}, 2000);
			});
		}
		
		gtgDetectionInProgress = true;

		const tagGateway = pmwDataLayer?.pixels?.google?.tag_gateway;
		
		// Priority 1: ALWAYS check measurement_path for Cloudflare (external proxy)
		// External/Cloudflare is the fastest handler and can be added at any time
		// via DNS change, so JS must always check for it — never skip this.
		// Cloudflare won't send X-PMW-GTG-Handler header, so we can detect it.
		if (tagGateway?.measurement_path) {
			const healthUrl = tagGateway.measurement_path + "/healthy";
			
			try {
				const response = await fetch(healthUrl, {method: "GET", cache: "no-store"});
				const handler = response.headers.get("X-PMW-GTG-Handler");

				// If no handler header, it's likely Cloudflare (external)
				if (!handler) {
					cacheGtgHandler("external");
					pmw.console.log("GTG handler: external (CDN/edge proxy)");
					return "external";
				}

				// For standalone or wordpress detected on measurement_path,
				// fall through to Priority 2 (server hint) to allow PHP-side
				// overrides (e.g. pmw_gtg_handler filter) to take effect.
				if (handler === "standalone" || handler === "wordpress") {
					pmw.console.log(handler + " proxy detected on measurement_path, checking for server hint...");
				}
			} catch (e) {
				pmw.console.log("measurement_path not accessible:", e.message);
				// Fall through to check server hint or standalone proxy
			}
		}

		// Priority 2: Use server-provided handler hint from PHP data layer
		// The server can reliably detect standalone vs wordpress — only Cloudflare/external
		// is undetectable server-side (because server-to-server requests bypass CDN).
		// If a server hint exists, trust it and skip the standalone proxy health check
		// entirely — this prevents 503/4xx errors on hosts that block direct PHP access.
		if (tagGateway?.handler && VALID_HANDLERS.includes(tagGateway.handler)) {
			cacheGtgHandler(tagGateway.handler);
			pmw.console.log("GTG handler: " + tagGateway.handler + " (server-detected)");
			return tagGateway.handler;
		}
		
		// Priority 3: Check proxy_url for standalone proxy (direct PHP file access)
		// Only reached when no server hint is available (e.g., fresh install, transient expired)
		if (tagGateway?.proxy_url) {
			const healthUrl = tagGateway.proxy_url + "?healthCheck=1";
			try {
				const response = await fetch(healthUrl, {method: "GET", cache: "no-store"});
				const handler = response.headers.get("X-PMW-GTG-Handler");

				if (handler === "standalone") {
					cacheGtgHandler("standalone");
					pmw.console.log("GTG handler: standalone (direct PHP proxy)");
					return "standalone";
				}
			} catch (e) {
				pmw.console.log("proxy_url not accessible:", e.message);
			}
		}
		
		// Priority 4: Fallback to WordPress proxy (via measurement_path)
		cacheGtgHandler("wordpress");
		pmw.console.log("GTG handler: wordpress (WordPress fallback proxy)");
		return "wordpress";
	};

	/**
	 * Get the Google Tag Gateway URL for loading gtag.js
	 *
	 * This is now an async function that waits for handler detection
	 * before returning the optimal URL.
	 *
	 * Priority (fastest to slowest):
	 * - "external" (Cloudflare) -> use measurement_path (fastest)
	 * - "standalone" -> use proxy_url (fast, bypasses WordPress)
	 * - "wordpress" -> use measurement_path (slowest, full WordPress load)
	 *
	 * @returns {Promise<string>} The gateway URL or empty string if not configured.
	 */
	pmw.getGoogleTagGatewayUrlAsync = async () => {

		const tagId = pmw.getGoogleGtagId();
		if (!tagId) {
			return "";
		}

		const tagGateway = pmwDataLayer?.pixels?.google?.tag_gateway;
		if (!tagGateway?.measurement_path) {
			return "";
		}

		// Check server-provided or cached handler first
		let handler = getGtgHandler();
		
		// If no handler available, do async detection
		if (!handler) {
			handler = await detectGtgHandler();
		}

		// External (Cloudflare) - use measurement_path (fastest)
		if (handler === "external") {
			pmw.console.log("Using measurement_path for GTG (external/CDN proxy)");
			return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}
		
		// Standalone proxy - use proxy_url (fast, bypasses WordPress)
		if (handler === "standalone" && tagGateway.proxy_url) {
			pmw.console.log("Using proxy_url for GTG (standalone proxy)");
			return tagGateway.proxy_url + "?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}

		// WordPress fallback - use measurement_path (slowest)
		pmw.console.log("Using measurement_path for GTG (WordPress fallback proxy)");
		return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
	};

	/**
	 * Get the Google Tag Gateway URL (synchronous version for backward compatibility)
	 * 
	 * Uses server-provided or cached value if available, otherwise falls back to measurement_path.
	 * The async version should be preferred for first page loads.
	 *
	 * @returns {string} The gateway URL or empty string if not configured.
	 */
	pmw.getGoogleTagGatewayUrl = () => {

		const tagId = pmw.getGoogleGtagId();
		if (!tagId) {
			return "";
		}

		const tagGateway = pmwDataLayer?.pixels?.google?.tag_gateway;
		if (!tagGateway?.measurement_path) {
			return "";
		}

		// Check server-provided or cached handler to determine which URL to use
		const handler = getGtgHandler();

		// If external (Cloudflare) is detected, always use measurement_path
		if (handler === "external") {
			return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}

		// If isolated detected, use direct proxy URL for best performance
		if (handler === "isolated" && tagGateway.proxy_url) {
			return tagGateway.proxy_url + "?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
		}

		// Default: use measurement_path (for WordPress fallback or unknown handler)
		return tagGateway.measurement_path + "/?id=" + encodeURIComponent(tagId) + "&s=/gtag/js";
	};

	/**
	 * Get the Google tracking URL (async version)
	 * 
	 * @returns {Promise<string>} The tracking URL
	 */
	pmw.getGoogleTrackingUrlAsync = async () => {

		// Skip the gateway proxy for bot user agents to reduce server load
		// Bots will use the standard googletagmanager.com URL directly
		if (pmw.isBotUserAgent()) {
			return pmw.getGoogleStandardTrackingUrl();
		}

		// Try to use the Google Tag Gateway (local proxy to FPS)
		const gatewayUrl = await pmw.getGoogleTagGatewayUrlAsync();
		if (gatewayUrl) {
			return gatewayUrl;
		}

		return pmw.getGoogleStandardTrackingUrl();
	};

	pmw.getGoogleTrackingUrl = () => {

		// Skip the gateway proxy for bot user agents to reduce server load
		// Bots will use the standard googletagmanager.com URL directly
		if (pmw.isBotUserAgent()) {
			return pmw.getGoogleStandardTrackingUrl();
		}

		// Try to use the Google Tag Gateway (local proxy to FPS)
		const gatewayUrl = pmw.getGoogleTagGatewayUrl();
		if (gatewayUrl) {
			return gatewayUrl;
		}

		return pmw.getGoogleStandardTrackingUrl();
	};

	pmw.getGoogleStandardTrackingUrl = () => {
		return "https://www.googletagmanager.com/gtag/js?id=" + pmw.getGoogleGtagId();
	}

	/**
	 * Attach the enhanced conversion user data to the Google tag.
	 *
	 * gtag("set", "user_data", …) applies to every event queued after it, so it must
	 * run before the purchase event is sent.
	 *
	 * There are two moments when the order data becomes available, and both have to
	 * be covered:
	 *
	 * 1. On the order confirmation page the server renders the order into the data
	 *    layer, so the data is there while the Google tag is being set up.
	 * 2. When the buyer never reaches the confirmation page, Automatic Conversion
	 *    Recovery fetches the order later through an AJAX round-trip, long after the
	 *    tag was set up. Without this second call the recovered conversion would go
	 *    out without user data, which is what Google Ads reports as "missing user
	 *    fields".
	 *
	 * Idempotent: the data is identical in both cases, so the first call wins.
	 *
	 * https://support.google.com/google-ads/answer/9888145#zippy=%2Cvalidate-your-implementation-using-chrome-developer-tools
	 * GA4: https://support.google.com/analytics/answer/14171598
	 *
	 * @since 1.64.1
	 */
	pmw.setGoogleEnhancedConversionData = () => {

		if (pmw.googleEnhancedConversionDataIsSet) return;
		if (!pmwDataLayer?.order?.google?.enhanced_conversion_data) return;

		gtag("set", "user_data", pmwDataLayer.order.google.enhanced_conversion_data);

		pmw.googleEnhancedConversionDataIsSet = true;

		pmw.console.log("Google: enhanced conversion user data set");
	};

	pmw.loadGoogle = async () => {

		if (!pmw.isGoogleActive()) {
			return;
		}

		pmwDataLayer.pixels.google.state = "loading";

		// Wait for GTG handler detection before determining the URL
		// This ensures we use the correct proxy URL even on first page load
		const trackingUrl = await pmw.getGoogleTrackingUrlAsync();

		pmw.console.log("Loading Google gtag.js from", trackingUrl.startsWith("/") ? window.location.origin + trackingUrl : trackingUrl);

		// Set up the gtag bootstrap (dataLayer, consent default, config) synchronously
		// and BEFORE the gtag.js library is injected.
		//
		// Google Consent Mode requires the consent "default" command to be present
		// before the library loads and before any config/event command. If the default
		// is set afterwards (e.g. in the script onload callback) Google ignores it —
		// "if your consent code is called out of order, consent defaults won't work" —
		// and the measurement hits stay buffered behind wait_for_update until an
		// explicit "update" arrives, so nothing reaches GA4 / Google Ads. Queuing
		// default → js → config → update into the dataLayer before injecting gtag.js
		// reproduces the canonical inline-snippet order.
		// https://developers.google.com/tag-platform/security/guides/consent
		try {

			// If Google TCF is active, output the TCF script
			// https://developers.google.com/tag-platform/security/guides/implement-TCF-strings
			if (pmwDataLayer?.pixels?.google?.tcf_support) {
				window["gtag_enable_tcf_support"] = true;
			}

			// Initiate Google dataLayer and gtag
			window.dataLayer = window.dataLayer || [];

			window.gtag = function gtag() {

				pmw.console.log("gtag called with", arguments);

				// Optional call: window.gtag stays installed for the life of
				// the page, and anything on the page may call it (a CMP, a tag
				// manager, a theme). Reaching into pmw.consent.api
				// unconditionally turned a foreign gtag() call made before or
				// after our consent module into a page error.
				//
				// @since 1.67.1
				pmw.consent?.api?.processExternalGcmConsentUpdate_experimental?.(arguments);

				// Delete the source property from the arguments object
				// It is a helper property that is not needed by gtag for regular calls
				if (arguments.length === 3 && arguments[2]?.source) {
					delete arguments[2].source;
				}

				dataLayer.push(arguments);
			};

			// Google Consent Mode
			if (pmwDataLayer?.pixels?.google?.consent_mode?.is_active) {

				const isExplicit = pmw.consent.explicitConsentModeActive();

				let google_consent_settings;

				if (isExplicit) {
					// Explicit mode: always default to "denied" for compliance optics.
					// Audit tools must see "Default State: denied" for all non-essential signals.
					google_consent_settings = {
						ad_personalization     : "denied",
						ad_storage             : "denied",
						ad_user_data           : "denied",
						analytics_storage      : "denied",
						functionality_storage  : "denied",
						personalization_storage: "denied",
						security_storage       : "granted",
						wait_for_update        : pmwDataLayer.pixels.google.consent_mode.wait_for_update,
					};
				} else {
					// Implicit mode: use live consent state (all "granted" unless user opted out)
					google_consent_settings = {
						ad_personalization     : pmw.consent.categories.get().marketing ? "granted" : "denied",
						ad_storage             : pmw.consent.categories.get().marketing ? "granted" : "denied",
						ad_user_data           : pmw.consent.categories.get().marketing ? "granted" : "denied",
						analytics_storage      : pmw.consent.categories.get().statistics ? "granted" : "denied",
						functionality_storage  : pmw.consent.categories.get().preferences ? "granted" : "denied",
						personalization_storage: pmw.consent.categories.get().marketing ? "granted" : "denied",
						security_storage       : pmw.consent.categories.get().necessary ? "granted" : "denied",
						wait_for_update        : pmwDataLayer.pixels.google.consent_mode.wait_for_update,
					};
				}

				if (pmwDataLayer?.general?.consent_management?.restricted_regions) {
					google_consent_settings.region = pmwDataLayer.general.consent_management.restricted_regions;
				}

				pmw.console.log("Google Consent Mode settings", google_consent_settings);

				gtag("consent", "default", google_consent_settings);
				gtag("set", "ads_data_redaction", pmwDataLayer.pixels.google.consent_mode.ads_data_redaction);
				gtag("set", "url_passthrough", pmwDataLayer.pixels.google.consent_mode.url_passthrough);

				// Fire the consent "update" that reflects the resolved consent state.
				// This is the "default → update" transition Google Consent Mode waits
				// for before releasing the hits buffered behind wait_for_update.
				if (isExplicit) {
					// Explicit mode: only update once the visitor has actually chosen
					// (a CMP cookie is present), preserving the "default denied → update"
					// pattern. First-time visitors stay denied until the CMP decides.
					if (pmw.consent.settings.get().visitorHasChosen) {
						pmw.updateGoogleConsentMode({});
					}
				} else {
					// Implicit mode: the resolved consent IS the live state (region unlock
					// and CMP cookies are already applied before pixels load). Without this
					// update no transition ever follows the default, so the GA4 / Google Ads
					// hits stay withheld for the whole page. updateGoogleConsentMode reads
					// the live consent getters, so a region-restricted visitor correctly
					// updates to denied rather than granted.
					pmw.updateGoogleConsentMode({});
				}
			}

			// Google Linker
			// https://developers.google.com/gtagjs/devguide/linker
			if (pmwDataLayer?.pixels?.google?.linker?.settings) {
				gtag("set", "linker", pmwDataLayer.pixels.google.linker.settings);
			}

			gtag("js", new Date());
			gtag("set", "developer_id.dNDI5Yz", true);

			// Set the enhanced conversion data for Google Ads and GA4. On the order
			// confirmation page the order is already in the data layer at this point.
			pmw.setGoogleEnhancedConversionData();

			// Google Ads loader
			if (!pmw.isEmptyObject(pmwDataLayer?.pixels?.google?.ads?.conversion_ids)) {  // Only run if the pixel has set up
				if (pmw.googleConfigConditionsMet({type: "marketing"})) {  							// Only run if cookie consent has been given
					pmw.fireGtagGoogleAds();
				} else {
					pmw.consent.logSuppressedPixel("marketing", "Google Ads");
				}
			}

			// GA4 loader
			if (pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id) {  			// Only run if the pixel has set up

				if (pmw.googleConfigConditionsMet({type: "statistics"})) {						// Only run if cookie consent has been given
					pmw.fireGtagGoogleAnalyticsGA4();
				} else {
					pmw.consent.logSuppressedPixel("statistics", "GA4");
				}
			}

		} catch (e) {
			console.error(e);
		}

		// Inject the gtag.js library only after the dataLayer queue (consent default →
		// js → config → update) is in place, so gtag.js processes it in the correct order.
		pmw.loadScriptAndCacheIt(trackingUrl, pmw.getGoogleStandardTrackingUrl())
			.then(() => {
				pmwDataLayer.pixels.google.state = "ready";
			})
			.catch(e => {
				// A native promise catch receives a single reason, not jQuery's
				// (jqXHR, textStatus, errorThrown) triple.
				pmw.console.error("The URL " + trackingUrl + " could not be loaded.", e && e.message ? e.message : e);
				pmwDataLayer.pixels.google.state = "error";
			});
	};

	pmw.gtagLoaded = () => {
		return new Promise((resolve, reject) => {

			// If Google is not active, reject the promise
			if (!pmw.isGoogleActive()) return reject(new Error("Google is not active"));

			let startTime = 0;
			let timeout   = 5000;
			let frequency = 200;

			(function wait() {
				if (pmwDataLayer?.pixels?.google?.state === "ready") return resolve();
				if (startTime >= timeout) {
					// Reject with a reason: a bare reject() surfaces in error trackers as an
					// undebuggable "Non-Error promise rejection captured with value: undefined".
					return reject(new Error("Google gtag failed to load. Probably a third party script is blocking it."));
				}
				startTime += frequency;
				setTimeout(wait, frequency);
			})();
		});
	};

	/**
	 * Terminal catch handler for pmw.gtagLoaded() chains.
	 *
	 * Every Google event waits for the tag before it fires. Without a catch the
	 * rejection of a blocked or never-loading tag escapes as an unhandled promise
	 * rejection, which error trackers record on every affected page view.
	 *
	 * @param description What was not sent, e.g. "Google Ads: view_item"
	 *
	 * @return {function(*): void}
	 */
	pmw.gtagNotLoaded = description => e => {
		pmw.console.error(description + " event not sent.", e && e.message ? e.message : e);
	};

	/**
	 * Update Google Consent Mode
	 *
	 * @param statistics
	 * @param marketing
	 * @param preferences
	 * @param necessary
	 */
	pmw.updateGoogleConsentMode = ({
									   statistics = pmw.consent.categories.get().statistics,
									   marketing = pmw.consent.categories.get().marketing,
									   preferences = pmw.consent.categories.get().preferences,
									   necessary = pmw.consent.categories.get().necessary,
								   }) => {

		try {
			if (!window.gtag) return;

			let data = {
				analytics_storage      : statistics ? "granted" : "denied",
				ad_storage             : marketing ? "granted" : "denied",
				ad_user_data           : marketing ? "granted" : "denied",
				ad_personalization     : marketing ? "granted" : "denied",
				functionality_storage  : preferences ? "granted" : "denied",
				personalization_storage: marketing ? "granted" : "denied",
				security_storage       : necessary ? "granted" : "denied",
				source                 : "pmw",
			};

			gtag("consent", "update", data);
			pmw.console.log("Google Consent Mode updated", data);
		} catch (e) {
			console.error(e);
		}
	};


}(window.pmw = window.pmw || {}));
